import React from 'react';
import type { UserPresenceDto, DevicePresenceSummary } from '../services/tenantApi';
import './PresenceDot.css';

export type PresenceDotState = 'live' | 'stale' | 'none' | 'unknown';
type DisplayPresence = Pick<DevicePresenceSummary, 'state' | 'lastSeenAtUtc' | 'devices'>;

export function presenceDotState(presence?: Pick<UserPresenceDto, 'state'> | null): PresenceDotState {
  switch (String(presence?.state || '').toLowerCase()) {
    case 'live': return 'live';
    case 'stale': return 'stale';
    case 'none': return 'none';
    default: return 'unknown';
  }
}

export function formatAgo(iso: string | null | undefined, nowMs: number): string | null {
  if (!iso) return null;
  const at = Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
  if (!Number.isFinite(at)) return null;
  const minutes = Math.max(0, Math.round((nowMs - at) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

const platformLabel = (platform?: string | null) => {
  const value = String(platform || '').toLowerCase();
  if (value === 'ios') return 'iOS';
  if (value === 'android') return 'Android';
  return platform || 'Device';
};

/** Tooltip such as "Live – last seen 3 min ago – Android 1.4.2". */
export function presenceTooltip(presence: DisplayPresence | null | undefined, nowMs: number = Date.now()): string {
  const state = presenceDotState(presence);
  if (state === 'unknown') return 'Presence unknown';
  if (state === 'none' || !presence) return 'No registered mobile device';
  const device = (presence.devices || [])
    .slice()
    .sort((a, b) => Date.parse(b.lastSeenAtUtc || '') - Date.parse(a.lastSeenAtUtc || ''))[0];
  const ago = formatAgo(presence.lastSeenAtUtc ?? device?.lastSeenAtUtc, nowMs);
  const parts = [state === 'live' ? 'Live' : 'Stale', ago ? `last seen ${ago}` : 'never seen'];
  if (device) parts.push(`${platformLabel(device.platform)}${device.appVersion ? ` ${device.appVersion}` : ''}`);
  if (state === 'stale' && device?.notLiveReason) parts.push(device.notLiveReason);
  return parts.join(' – ');
}

interface PresenceDotProps {
  presence?: DisplayPresence | null;
  nowMs?: number;
}

/** Green = live device, amber = registered but stale, grey = no device (BL-043 UI-060). */
export const PresenceDot: React.FC<PresenceDotProps> = ({ presence, nowMs }) => {
  const state = presenceDotState(presence);
  const tooltip = presenceTooltip(presence, nowMs ?? Date.now());
  return (
    <span
      className={`presence-dot presence-dot-${state}`}
      data-presence-state={state}
      title={tooltip}
      aria-label={tooltip}
      role="img"
    />
  );
};

export default PresenceDot;
